import type { Role } from "./types";
import { buildWorkspaceWeeks } from "./expectedSlots";

export const lifecycleStages = ["configuration", "ideation", "briefing", "design", "clientReview"] as const;

export type LifecycleStage = (typeof lifecycleStages)[number];
export type LifecycleActorRole = "admin" | "am" | "designer" | "client";
export type LifecycleMonthSubscriptionScope = "ideation" | "workbench";

export type LifecycleStagePolicy = {
  accessible: boolean;
  notStarted?: boolean;
  complete?: boolean;
  eligibleCount?: number;
  inProgressCount?: number;
  reason?: string;
};

export type ClientLifecycleItemInput = {
  _id: string;
  snapshotTitle: string;
  snapshotContentType: "story" | "post" | "reel";
  snapshotDateLabel?: string;
  snapshotClientCaption: string;
  snapshotAssetFileName: string;
  snapshotDisplayFiles?: Array<{
    sortOrder: number;
    fileName: string;
    mimeType: string;
    width?: number;
    height?: number;
    altText?: string;
  }>;
  clientStatus: "not_reviewed" | "changes_requested" | "approved" | "scheduled" | "rejected";
  sortOrder: number;
};

export function resolveLifecycleActorRole(roles: readonly Role[]): LifecycleActorRole {
  if (roles.includes("agency_admin")) return "admin";
  if (roles.includes("account_manager")) return "am";
  if (roles.includes("designer")) return "designer";
  return "client";
}

export function lifecyclePolicyKey(stage: LifecycleStage) {
  return stage === "ideation" ? "calendar" : stage;
}

/**
 * Briefing and Design consume the same lifecycle month read model. Keeping
 * that shared scope in the subscription key lets the shell and page reuse
 * one Convex result while Calendar and Client Review retain their route
 * specific read behavior.
 */
export function lifecycleMonthSubscriptionScope(stage: LifecycleStage): LifecycleMonthSubscriptionScope | undefined {
  if (stage === "ideation") return "ideation";
  if (stage === "briefing" || stage === "design") return "workbench";
  return undefined;
}

export function lifecycleRouteSlug(stage: LifecycleStage) {
  if (stage === "configuration") return "proposal-configuration";
  if (stage === "ideation") return "calendar";
  if (stage === "clientReview") return "client-review";
  return stage;
}

export function getLifecycleRouteAccess({
  actorRole,
  policy,
  stage,
}: {
  actorRole: LifecycleActorRole;
  policy?: LifecycleStagePolicy;
  stage: LifecycleStage;
}) {
  if (actorRole === "client" && stage !== "clientReview") {
    return {
      allowed: false,
      reason: "Client access is limited to Client Review.",
    };
  }
  if (policy && !policy.accessible && !(stage === "briefing" && policy.notStarted)) {
    return {
      allowed: false,
      reason: policy.reason ?? `${stage} is not available yet.`,
    };
  }
  return { allowed: true, reason: undefined };
}

export function toClientLifecycleItem(item: ClientLifecycleItemInput, destinations: readonly string[] = []) {
  return {
    key: item._id,
    title: item.snapshotTitle,
    contentType: item.snapshotContentType,
    scheduledDate: item.snapshotDateLabel,
    caption: item.snapshotClientCaption,
    asset: {
      fileName: item.snapshotAssetFileName,
      files: [...(item.snapshotDisplayFiles ?? [])]
        .sort((left, right) => left.sortOrder - right.sortOrder)
        .map((file) => ({
          fileName: file.fileName,
          mimeType: file.mimeType,
          width: file.width,
          height: file.height,
          altText: file.altText,
        })),
    },
    platforms: [...destinations],
    status: item.clientStatus === "not_reviewed" ? "awaiting_review" : item.clientStatus,
    sortOrder: item.sortOrder,
  };
}

export function normalizeLifecycleText(value: string | undefined) {
  return (value ?? "").trim().replace(/\s+/g, " ");
}

export function isLifecycleTextChange(previous: string | undefined, next: string | undefined) {
  return normalizeLifecycleText(previous) !== normalizeLifecycleText(next);
}

export function isClientReviewApproved(status: string | undefined) {
  return status === "approved" || status === "client_approved";
}

export function isClientReviewComplete(status: string | undefined) {
  return isClientReviewApproved(status) || status === "scheduled";
}

export function isClientReviewReopenable(status: string | undefined) {
  return status === "changes_requested" || status === "scheduled" || isClientReviewApproved(status);
}

export function monthWeekForDate(date: string | undefined, month: string) {
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return undefined;
  return buildWorkspaceWeeks(month).find((week) => date >= week.startsOn && date <= week.endsOn)?.week;
}

/**
 * Package-truth reasons are prefixed with "Outdated package · " so they read
 * on their own elsewhere. The Briefing notice already renders that as its
 * heading, so strip the prefix here instead of printing it twice.
 */
export function outdatedPackageDetail(reason?: string) {
  const text = (reason ?? "Outdated package · Binding unknown").trim();
  const prefix = "Outdated package · ";
  return text.startsWith(prefix) ? text.slice(prefix.length) : text;
}
